import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, Input, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { ENV_CONFIG } from '@ottobock/shared/domain';

import { StepperFormStore } from '../../../+state/firmware-package/stepper-form.store';
import { CompatibleVersionInput } from '../../../entities/models/firmware-stepper.model';
import { isCompatibleVersionInvalid } from '../../../helpers';
import { isCompatibleVersionIdInvalid } from '../../../helpers/checkVersionIdForValidity';

@Component({
  standalone: true,
  selector: 'remote-update-domain-compatibility',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    CommonModule,
    MatRadioModule,
    MatButtonToggleModule,
    FormsModule,
    MatButtonModule
  ],
  templateUrl: './device-compatibility.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './device-compatibility.css'
})
export class DeviceCompatibility implements OnInit {
  private store = inject(StepperFormStore);
  private envConfig = inject(ENV_CONFIG);
  @Input() currentSection = '';
  versionIsIdenticalOrExceeding = computed(() => this.store.IsIdenticalOrExceedingVersion());
  showError = computed(() => this.store.showCompatibilityError());
  dialog = inject(MatDialog);

  versionRegex = /^\d+-\d+,\d+\.\d+\.\d+$/;

  inputs: CompatibleVersionInput[] = [
    {
      id: 1,
      value: '',
      namePatternInvalid: false,
      touched: false,
      isExceeding: false,
      isVersionIdMismatch: false
    }
  ];
  private nextId = 2;

  ngOnInit(): void {
    const existingVersions = this.store.deviceCompatibility().compatible_device_software_versions;
    if (existingVersions.length) {
      this.inputs = existingVersions.map(v => {
        const isSameAsManifest = this.envConfig.allowCompatibilityDowngrade
          ? false
          : v.value.trim() === this.version.trim();
        const isExceedingManifest = this.envConfig.allowCompatibilityDowngrade
          ? false
          : isCompatibleVersionInvalid(this.version.trim(), v.value.trim());

        return {
          id: v.id,
          value: v.value,
          namePatternInvalid: !this.versionRegex.test(v.value),
          touched: false,
          isExceeding: isSameAsManifest || isExceedingManifest,
          isVersionIdMismatch: this.envConfig.allowCompatibilityDowngrade
            ? false
            : isCompatibleVersionIdInvalid(this.version, v.value)
        };
      });
      this.nextId = Math.max(...existingVersions.map(v => v.id)) + 1;

      const allValid = this.inputs.every(i => !i.namePatternInvalid && i.value.trim() !== '');
      const hasExceedingVersion = this.inputs.some(i => i.isExceeding);
      const hasAnyVersionMismatch = this.inputs.some(i => i.isVersionIdMismatch);

      this.store.setHasVersionIdMismatch(hasAnyVersionMismatch);
      this.store.setIsIdenticalOrExceedingVersion(hasExceedingVersion);

      // Mark as invalid if there are exceeding versions
      this.store.updateDeviceCompatibility({ isValid: allValid && !hasExceedingVersion }, '');
    } else {
      this.inputs = [
        {
          id: this.nextId++,
          value: '',
          namePatternInvalid: false,
          touched: false,
          isExceeding: false,
          isVersionIdMismatch: false
        }
      ];
    }
  }

  checkVersion(input: CompatibleVersionInput) {
    input.touched = true;

    const raw = input.value?.trim() ?? '';
    const isEmpty = raw === '';
    const failsPattern = !this.versionRegex.test(raw);

    const duplicateExists = this.inputs.some(
      version => version.id !== input.id && version.value.trim() === raw
    );

    const isSameAsManifestVersion = this.envConfig.allowCompatibilityDowngrade
      ? false
      : raw === this.version.trim();

    const isCompatibleVersion = this.envConfig.allowCompatibilityDowngrade
      ? false
      : isCompatibleVersionInvalid(this.version.trim(), raw);

    input.isVersionIdMismatch = this.envConfig.allowCompatibilityDowngrade
      ? false
      : isCompatibleVersionIdInvalid(this.version.trim(), raw);

    const isIdenticalOrExceedingVersion = isSameAsManifestVersion || isCompatibleVersion;

    input.isExceeding = isIdenticalOrExceedingVersion;

    // update global store flag to true if there is an input with mismatching id
    const hasAnyVersionMismatch = this.inputs.some(i => i.isVersionIdMismatch);
    this.store.setHasVersionIdMismatch(hasAnyVersionMismatch);

    // Update global store flag to true if ANY input is exceeding
    const isAnyInputExceeding = this.inputs.some(i => i.isExceeding);
    this.store.setIsIdenticalOrExceedingVersion(isAnyInputExceeding);

    // Keep visible error hidden until user clicks Next
    this.store.setShowCompatibilityError(false);

    // mark invalid for the input only if pattern/empty/duplicate
    input.namePatternInvalid = isEmpty || failsPattern || duplicateExists;

    if (!input.namePatternInvalid) {
      this.store.updateDeviceCompatibilityArray(input.id, input.value);
    } else {
      this.store.removeCompatibleVersionById(input.id);
      this.store.setIsIdenticalOrExceedingVersion(false);
    }

    this.updateGlobalValidity();
  }

  trackById(_index: number, item: CompatibleVersionInput): number {
    return item.id;
  }

  addNewInput() {
    this.inputs.push({
      id: this.nextId++,
      value: '',
      namePatternInvalid: false,
      touched: false,
      isExceeding: false
    });
    this.updateGlobalValidity();
  }

  removeInput(index: number) {
    if (index > 0) {
      const removed = this.inputs.splice(index, 1)[0];
      this.store.removeCompatibleVersionById(removed.id);
      this.updateGlobalValidity();
      this.updateExceedingState();
    }
  }

  private updateGlobalValidity() {
    if (this.inputs.length === 0) {
      this.store.updateDeviceCompatibility({ isValid: false }, '');
      return;
    }

    const allValid = this.inputs.every(i => !i.namePatternInvalid && i.value.trim() !== '');

    const hasExceedingVersion = this.inputs.some(i => i.isExceeding);

    if (!allValid || hasExceedingVersion) {
      this.store.updateDeviceCompatibility({ isValid: false }, '');
    } else {
      this.store.updateDeviceCompatibility({ isValid: true }, '');
    }
  }

  private updateExceedingState() {
    const isAnyInputExceeding = this.inputs.some(i => i.isExceeding);
    this.store.setIsIdenticalOrExceedingVersion(isAnyInputExceeding);
  }

  get error(): string | undefined {
    return this.store.errors().deviceCompatibility;
  }

  get version(): string {
    return this.store.getManifestVersion().version;
  }
}
